import type {Icon} from '@primer/octicons-react'
import {
  NoEntryFillIcon,
  CheckCircleFillIcon,
  DotFillIcon,
  GitMergeIcon,
  GitMergeQueueIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  AlertFillIcon,
} from '@primer/octicons-react'
import {Label, type LabelColorOptions} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import iconStyles from './EntryStatusIcon.module.css'
import labelStyles from './EntryStatusLabel.module.css'
import type {
  StackSectionEntryType,
  StackSectionEntryStatus,
} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import type {StackRebasePullRequestStatus} from '../../../hooks/mutations/use-stack-rebase-mutation'

export type EntryIconInfo =
  | {type: 'icon'; icon: Icon; className: string; ariaLabel?: string}
  | {type: 'spinner'; className?: string}

export type EntryStatusInfo = {
  label: {variant: LabelColorOptions; text: string} | null
  icon: EntryIconInfo
}

const NoEntryFillIconDangerDetails: EntryIconInfo = {
  type: 'icon',
  icon: NoEntryFillIcon,
  className: 'color-fg-danger',
}

export const ENTRY_STATUS_INFO: Record<StackSectionEntryStatus, EntryStatusInfo> = {
  IRRELEVANT_MERGED: {
    label: {variant: 'done', text: 'Merged'},
    icon: {type: 'icon', icon: GitMergeIcon, className: 'color-fg-done'},
  },
  IRRELEVANT_AFTER: {
    label: null,
    icon: {
      type: 'icon',
      icon: GitPullRequestIcon,
      className: 'color-fg-muted',
      ariaLabel: 'Will not merge: higher up the stack',
    },
  },
  IRRELEVANT_DRAFT: {
    label: {variant: 'secondary', text: 'Draft'},
    icon: {type: 'icon', icon: GitPullRequestDraftIcon, className: 'color-fg-muted', ariaLabel: 'Cannot merge'},
  },
  IRRELEVANT_QUEUED: {
    label: {variant: 'attention', text: 'Queued'},
    icon: {type: 'icon', icon: GitMergeQueueIcon, className: 'color-fg-attention', ariaLabel: 'Merge status'},
  },
  BLOCKED_STACK: {
    label: {variant: 'attention', text: 'Needs rebase'},
    icon: {type: 'icon', icon: AlertFillIcon, className: 'color-fg-attention', ariaLabel: 'Cannot merge'},
  },
  OK: {
    label: {variant: 'success', text: 'Able to merge'},
    icon: {type: 'icon', icon: CheckCircleFillIcon, className: 'color-fg-success'},
  },
  LOADING_MERGE_STATE: {
    label: null,
    icon: {type: 'spinner'},
  },
  ERROR_MERGE_STATE: {
    label: {variant: 'attention', text: 'Error loading data'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Cannot load mergeability data'},
  },
  BLOCKED_MERGE_STATE: {
    label: {variant: 'danger', text: 'Failing rules'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Cannot merge'},
  },
  UNKNOWN_MERGE_STATE: {
    label: {variant: 'default', text: 'Pending'},
    icon: {type: 'spinner'},
  },
  WAITING_ON_PREVIOUS: {
    label: {variant: 'attention', text: 'Waiting on previous'},
    icon: {
      type: 'icon',
      icon: DotFillIcon,
      className: `color-fg-attention ${iconStyles.scaledDot}`,
      ariaLabel: 'Waiting on previous pull request',
    },
  },
  AWAITING_APPROVAL: {
    label: {variant: 'danger', text: 'Awaiting approval'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Awaiting approval'},
  },
  MERGING: {
    label: {variant: 'done', text: 'Merging'},
    icon: {type: 'spinner', className: 'color-fg-done'},
  },
  CHECKS_PENDING: {
    label: {variant: 'attention', text: 'Checks pending'},
    icon: {
      type: 'icon',
      icon: DotFillIcon,
      // `scaledDot` renders the dot visually larger without widening the icon's
      // grid slot, which would otherwise push the row off the vertical timeline line.
      className: `color-fg-attention ${iconStyles.scaledDot}`,
      ariaLabel: 'Checks pending',
    },
  },
  CHECKS_FAILING: {
    label: {variant: 'danger', text: 'Checks failing'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Checks failing'},
  },
  FAILING_RULES: {
    label: {variant: 'danger', text: 'Failing rules'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Failing rules'},
  },
  MERGE_CONFLICTS: {
    label: {variant: 'danger', text: 'Merge conflicts'},
    icon: {...NoEntryFillIconDangerDetails, ariaLabel: 'Merge conflicts'},
  },
}

export function EntryStatusLabel({
  entry,
  rebaseState,
  allBelowCanMerge,
}: {
  entry: StackSectionEntryType
  rebaseState?: StackRebasePullRequestStatus
  allBelowCanMerge?: boolean
}) {
  const statusesV2Enabled = isFeatureEnabled('pull_request_stacks_statuses_v2')

  // Rebasing
  if (rebaseState === 'SUCCEEDED') {
    return (
      <Label variant="success" className={labelStyles.solid}>
        Rebased
      </Label>
    )
  } else if (rebaseState === 'CONFLICTED') {
    return (
      <Label variant="danger" className={labelStyles.solid}>
        Merge conflicts
      </Label>
    )
  } else if (rebaseState === 'FAILED') {
    return (
      <Label variant="danger" className={labelStyles.solid}>
        Rebase failed
      </Label>
    )
  } else if (rebaseState === 'PENDING') {
    return (
      <Label variant="default" className={labelStyles.solid}>
        Rebasing…
      </Label>
    )
  }

  // Use the special-case "Meets stack requirements" label for the current entry
  // only when `pull_request_stacks_statuses_v2` is disabled.
  if (!statusesV2Enabled && entry.status === 'OK' && entry.position === 'CURRENT') {
    return (
      <Label variant="success" className={labelStyles.solid}>
        Meets stack requirements
      </Label>
    )
  }

  if (!statusesV2Enabled && entry.status === 'OK' && entry.position === 'BEFORE') {
    return (
      <Label variant="success" className={labelStyles.solid}>
        {allBelowCanMerge ? 'Able to merge' : 'Ready to merge'}
      </Label>
    )
  }

  const {label} = ENTRY_STATUS_INFO[entry.status]
  if (!label) return null
  return (
    <Label variant={label.variant} className={labelStyles.solid}>
      {label.text}
    </Label>
  )
}
